import React, { useEffect, useState, useMemo } from 'react'
import { useQueryParams } from 'utils/hooks/useQueryParams'
import { useRightsStatsReport } from 'api/rights/queries/useRightsStatsReport'
import { Box, Stack } from '@mui/material'
import { ChartCard } from '../components/DataChart'
import { DataPieChart, PieChartDataItem } from '../components/DataPieChart'
import { Card, Icon, theme, IconDictionary } from '@ketch-com/deck'
import pluralize from 'pluralize'
import { SemiCircleChart } from '../components/SemiCircleChart'
import { LiveQueueFlowChart } from './LiveQueueFlowCharts'
import { LiveRequestsTable } from './LiveRequestsTable'
import { transformChartData } from '../utils/transformChartData'
import { useLiveRightsStatsReport } from 'api/rightsReports/queries/useLiveRightsStatsReport'
import { showToast } from 'components/modals/AlertComponent'
import { RightsFilters } from '../components/RightsFilters'
import { useFetchLiveRequestsPerJurisdiction } from 'api/rightsReports/queries/useLiveFetchRequestsPerJurisdiction'
import { useFetchLiveRequestsPerProperty } from 'api/rightsReports/queries/useFetchLiveRequestsPerProperty'

type PropertyDataItem = {
  applicationCode: string
  applicationName?: string
  total: number
  rights: {
    rightCode: string
    rightName?: string
    total: number
  }[]
}

type JurisdictionDataItem = {
  policyScopeCode: string
  policyScopeName?: string
  total?: number
  complete?: number
  rejected?: number
  rights: {
    rightCode: string
    rightName?: string
    total: number
  }[]
}

export interface GenericPieChartProps {
  data: PieChartDataItem[]
  isLoading: boolean
  showFallbackColors?: boolean
}

export const GenericPieChart: React.FC<GenericPieChartProps> = ({ data, isLoading, showFallbackColors = false }) => {
  const chartData = isLoading || !data || data.length === 0 ? [] : data
  return <DataPieChart data={chartData} showFallbackColors={showFallbackColors} />
}

export const LiveTab = () => {
  const { queries } = useQueryParams<{
    policyScope?: string
    dataSubjectType?: string
    rightCode?: string
    applicationCode?: string
  }>()
  const { policyScope, dataSubjectType, rightCode, applicationCode } = queries

  const [rightNameData, setRightNameData] = useState<any[]>([])
  const [rightTypeData, setRightTypeData] = useState<any[]>([])
  const [reportType, setReportType] = useState<'right' | 'canonical-right'>('right')

  const apiParams = useMemo(
    () => ({
      ...(policyScope && { policyScope }),
      ...(dataSubjectType && { dataSubjectType }),
      ...(rightCode && { rightCode }),
      ...(applicationCode && { applicationCode }),
    }),
    [policyScope, dataSubjectType, rightCode, applicationCode],
  )

  const { data: rightsReport, isLoading } = useRightsStatsReport({
    params: apiParams,
    onError: () => showToast({ content: 'Failed to fetch rights stats', type: 'error' }),
  })

  const { data: propertyDataRaw, isLoading: isLoadingProperty } = useFetchLiveRequestsPerProperty({
    params: apiParams,
    onError: () => showToast({ content: 'Failed to fetch property stats', type: 'error' }),
  })

  const { data: jurisdictionDataRaw, isLoading: isJurisdictionLoading } = useFetchLiveRequestsPerJurisdiction({
    params: apiParams,
    onError: () => showToast({ content: 'Failed to fetch jurisdiction stats', type: 'error' }),
  })

  const { data: rightStatsData, isLoading: isRequestsPerRightLoading } = useLiveRightsStatsReport({
    params: { ...apiParams, reportType },
    onError: () => showToast({ content: 'Failed to fetch right stats', type: 'error' }),
  })

  useEffect(() => {
    if (rightStatsData === null) {
      setRightNameData([])
      setRightTypeData([])
      return
    }

    if (reportType === 'right') {
      setRightNameData(rightStatsData)
    } else {
      setRightTypeData(rightStatsData)
    }
  }, [rightStatsData, reportType])

  const propertyData = propertyDataRaw?.rightInvocationStatsPerApplication ?? []
  const jurisdictionData = jurisdictionDataRaw?.rightInvocationStatsPerPolicyScope ?? []

  return (
    <Box pb={4}>
      <RightsFilters />
      <Stack direction="row" spacing={3} mt={3}>
        <Box sx={{ flex: 3.5 }}>
          <ChartCard title="Current Queue" isLoading={isLoading}>
            <GenericPieChart
              data={rightsReport ? transformChartData(rightsReport, true) : []}
              isLoading={isLoading}
              showFallbackColors
            />
          </ChartCard>
        </Box>
        <Box sx={{ flex: 3.5 }}>
          <SemiCircleChart rightsReport={rightsReport} isLoading={isLoading} />
        </Box>
        <Stack spacing={3} sx={{ flex: 3 }}>
          <Card
            cardVariant="stretchWidth"
            cardTitle={`${rightsReport?.avgFulfilTime ?? 0} ${pluralize('Day', rightsReport?.avgFulfilTime ?? 0)}`}
            subTitle="Mean Fulfillment Time"
            primaryIcon={
              <Icon name={IconDictionary.FCalendar} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
            }
            isNumerical
          />
          <Card
            cardVariant="stretchWidth"
            cardTitle={`${rightsReport?.medialFulfillTime || 0} ${pluralize(
              'Day',
              rightsReport?.medialFulfillTime || 0,
            )}`}
            subTitle="Median Fulfillment Time"
            primaryIcon={
              <Icon name={IconDictionary.OChart} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
            }
            backgroundColor={theme.palette.Black.o8}
            isNumerical
          />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3} mt={3}>
        <LiveRequestsTable
          reportType={reportType}
          setReportType={setReportType}
          rightNameData={rightNameData}
          rightTypeData={rightTypeData}
          isLoading={isRequestsPerRightLoading}
        />
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
        <ChartCard title="Request Volume By Jurisdictions" isLoading={isLoading}>
          <GenericPieChart
            data={jurisdictionData.map((scope: JurisdictionDataItem) => {
              const total = scope.total ?? (scope.complete ?? 0) + (scope.rejected ?? 0)
              return {
                name: scope.policyScopeName ?? scope.policyScopeCode,
                value: total,
                originalCount: total,
                rights: scope.rights.map(r => ({
                  rightCode: r.rightCode,
                  rightName: r.rightName ?? '',
                  total: r.total,
                })),
              }
            })}
            isLoading={isJurisdictionLoading}
          />
        </ChartCard>
        <LiveQueueFlowChart rightsReport={rightsReport || {}} isLoading={isLoading} />
        <ChartCard title="Request Volume By Property" isLoading={isLoading}>
          <GenericPieChart
            data={(propertyData ?? []).map((app: PropertyDataItem) => ({
              name: app.applicationName ?? app.applicationCode,
              value: app.total,
              originalCount: app.total,
              rights: app.rights.map(r => ({
                rightCode: r.rightCode,
                rightName: r.rightName ?? '',
                total: r.total,
              })),
            }))}
            isLoading={isLoadingProperty}
          />
        </ChartCard>
      </Stack>
    </Box>
  )
}
